import { AxiosResponse } from "axios";
import { logger } from "../../../utilities/logger";
import { axiosRetryPost } from "../../../utilities/AxiosRetryUtil";
import authModule from "../../../utilities/AuthModule";
import { tryDecodeUriComponent } from "../../../utilities/EncodeDecode";

interface ICancellationToken {
    web: string;
    isCancellationRequested: boolean;
}

export class CancellableSharePointRestApi {
    private cancellationToken: ICancellationToken;

    constructor(webUrl: string) {
        this.cancellationToken = {
            web: webUrl,
            isCancellationRequested: false
        };
    }

    getIsCancellationRequested(webUrl: string): boolean {
        if (this.cancellationToken.web === webUrl) {
            return this.cancellationToken.isCancellationRequested;
        }
        return false;
    }

    cancel(webUrl: string): void {
        if (this.cancellationToken.web === webUrl) {
            this.cancellationToken.isCancellationRequested = true;
        }
    }

    async getSharepointLibraryRootItemCount(webUrl: string, sharePointListId: string): Promise<number> {
        try {
            const baseUrl = `${webUrl}/_api/web/lists('${sharePointListId}')/RenderListDataAsStream`;

            const requestConfig = {
                headers: { Authorization: `Bearer ${await authModule.getSharePointAccessToken()}`, accept: "application/json; odata=nometadata" }
            };

            const requestData = {
                parameters: {
                    DatesInUtc: true,
                    ViewXml: "<View><ViewFields><FieldRef Name=\"ID\"/></ViewFields><RowLimit Paged=\"TRUE\">5000</RowLimit></View>"
                }
            };

            let retry: boolean;
            let nextPageUrl: string;
            let response: AxiosResponse<any>;

            let itemCount: number = 0;
            let retryCount: number = 0;

            do {
                if (this.cancellationToken.isCancellationRequested || this.cancellationToken.web !== webUrl) {
                    break;
                }

                let url: string;
                if (nextPageUrl) {
                    url = baseUrl + nextPageUrl;
                } else {
                    url = baseUrl;
                }

                try {
                    response = await axiosRetryPost(url, requestData, requestConfig, false);

                    if (Array.isArray(response?.data?.Row) && response.data.Row.length > 0) {
                        itemCount += response.data.Row.length;
                    }

                    nextPageUrl = response?.data?.NextHref;

                    if (!nextPageUrl) {
                        break;
                    }
                } catch (error) {
                    // Retry throttling.
                    if (error?.response?.data["odata.error"]?.code?.startsWith("-2147024860") && ++retryCount < 3) {
                        retry = true;
                        await new Promise(res => setTimeout(res, 1000));
                    }
                    else {
                        break;
                    }
                }
            } while (
                !this.cancellationToken.isCancellationRequested
                && this.cancellationToken.web === webUrl
                && (retry || nextPageUrl)
            );

            return itemCount;

        } catch (err) {
            logger(err, "SharePointRestService.ts (getSharePointFiles)");
        }

        return 0;
    };

    // scopeRecursiveAll flattens the folder tree (aggregate "All documents" probe —
    // its grid queries Scope="RecursiveAll", so its value search must too). Legacy
    // callers omit it and keep the folder-scoped view unchanged.
    /**
     * @param limits Optional stop conditions for the paging loop below, which otherwise
     *               walks the list to exhaustion — unbounded client work, and a list view
     *               threshold hazard on a large library. Omitted by the single-library
     *               call sites, whose behaviour is therefore unchanged; the aggregate
     *               panel passes bounds because it runs this across N libraries at once.
     */
    async getSharepointFilterValues(webUrl: string, sharePointListId: string, folderServerRelativeUrl: string, filterField: string, filterValue: string, scopeRecursiveAll: boolean = false, limits?: { maxPages?: number; maxValues?: number }): Promise<string[]> {
        try {
            const baseUrl = `${webUrl}/_api/web/lists('${sharePointListId}')/RenderListDataAsStream`;

            const requestConfig = {
                headers: { Authorization: `Bearer ${await authModule.getSharePointAccessToken()}`, accept: "application/json; odata=nometadata" }
            };

            const requestData = {
                parameters: {
                    DatesInUtc: true,
                    ViewXml: `<View${scopeRecursiveAll ? ' Scope="RecursiveAll"' : ''}><ViewFields><FieldRef Name="${filterField}"/></ViewFields><Query><Where><Contains id="${filterField}"><FieldRef Name="${filterField}"/><Value Type="Text">${filterValue}</Value></Contains></Where></Query><RowLimit Paged="TRUE">5000</RowLimit></View>`
                }
            };

            if (folderServerRelativeUrl) {
                requestData.parameters["FolderServerRelativeUrl"] = tryDecodeUriComponent(folderServerRelativeUrl);
            }

            let retry: boolean;
            let nextPageUrl: string;
            let response: AxiosResponse<any>;

            let retryCount: number = 0;

            const filterValues: string[] = [];
            // Unbounded unless the caller asks otherwise, so every existing call site
            // keeps its current behaviour exactly.
            const maxPages: number = limits?.maxPages ?? Number.POSITIVE_INFINITY;
            const maxValues: number = limits?.maxValues ?? Number.POSITIVE_INFINITY;
            let pagesFetched = 0;

            do {
                if (this.cancellationToken.isCancellationRequested || this.cancellationToken.web !== webUrl) {
                    break;
                }

                let url: string;
                if (nextPageUrl) {
                    url = baseUrl + nextPageUrl;
                } else {
                    url = baseUrl;
                }

                try {
                    response = await axiosRetryPost(url, requestData, requestConfig, false);

                    pagesFetched++;

                    if (Array.isArray(response?.data?.Row) && response.data.Row.length > 0) {
                        filterValues.push(...response.data.Row.map(item => item[filterField]));
                    }

                    // Counted on RAW rows, not distinct values: the point is to bound the
                    // WORK, and a field with one repeated value would otherwise never
                    // reach a distinct-value ceiling no matter how far it paged.
                    if (pagesFetched >= maxPages || filterValues.length >= maxValues) {
                        break;
                    }

                    nextPageUrl = response?.data?.NextHref;

                    if (!nextPageUrl) {
                        break;
                    }
                } catch (error) {
                    // Retry throttling.
                    if (error?.response?.data["odata.error"]?.code?.startsWith("-2147024860") && ++retryCount < 3) {
                        retry = true;
                        await new Promise(res => setTimeout(res, 1000));
                    }
                    else {
                        break;
                    }
                }
            } while (
                !this.cancellationToken.isCancellationRequested
                && this.cancellationToken.web === webUrl
                && (retry || nextPageUrl)
            );

            if (!this.cancellationToken.isCancellationRequested && this.cancellationToken.web === webUrl) {
                return filterValues.filter((v, i, a) => a.indexOf(v) === i);
            }

        } catch (err) {
            logger(err, "SharePointRestService.ts (getSharepointFilterValues)");
        }

        return [];
    };
}